import { useMemo, useState } from 'react'

import { NavMenuContentType } from '@hhgtech/hhg-components'
import { Text } from '@hhgtech/hhg-components/mantine'
import { Box, Stack } from '@mantine/core'
import { useIntersection } from '@mantine/hooks'
import innertext from 'innertext'
import dynamic from 'next/dynamic'
import { useIntl } from 'react-intl'

import { getHelloSitesUrl } from 'api/utils/paths'
import { Container } from 'components/atoms/container'
import { HealthToolBreadcrumb } from 'components/molecules/healthToolBreadcrumb'
import { HealthToolTypeCategory } from 'components/organisms/healthToolTypeCategory'
import MobileBottomNavigationWrapper from 'components/organisms/mobileBottomNavigationWrapper'
import ScrollingBlocks from 'components/organisms/scrollingBlocks'
import { HealthToolLayout } from 'components/templates/healthToolLayout'
import {
  getDefaultLanguageOptions,
  NavigatorLayout,
} from 'components/templates/navigatorLayout'
import { useRouterExtend } from 'hooks/useRouterExtend'
import { assetImagePath, LOCALE_SPECS, LOGO } from 'interfaces/constants'
import { HEALTH_TOOL_NAME } from 'interfaces/constants/healthTools'
import { FooterInfo, HealthTool, HealthToolByCategory } from 'interfaces/types'

import { HealthToolsNav } from './components/healthToolsNav'
import { StyledHealthToolsPageHome } from './index.styled'
import useStyles from './index.styles'
import { IntroToolsBanner } from './introToolsBanner'

export type Props = {
  healthToolsByCategory: HealthToolByCategory[]
  footerInfo?: FooterInfo
  dataNavMenuContent?: NavMenuContentType
}

const SubHeader = dynamic(
  () => import('components/molecules/subHeader').then((mod) => mod.SubHeader),
  {
    ssr: false,
  },
)

export const DISLAY_TOOL = [
  { name: HEALTH_TOOL_NAME.BMI, color: 'blue' },
  { name: HEALTH_TOOL_NAME.BMR, color: 'green' },
  { name: HEALTH_TOOL_NAME.THR, color: 'red' },
]

export const HealthTools = ({
  healthToolsByCategory,
  footerInfo,
  dataNavMenuContent,
}: Props) => {
  const { formatMessage: f } = useIntl()
  const { locale, asPath } = useRouterExtend()
  const { classes } = useStyles(undefined, {
    name: 'HealthTools',
  })
  const { ref, entry } = useIntersection({
    threshold: 1,
  })
  const siteName = LOCALE_SPECS[locale].SITE_NAME_FORMATTED
  const options = useMemo(() => {
    return healthToolsByCategory.reduce<HealthToolByCategory[]>(
      (items, tool) => {
        if (tool.healthTools.length > 0) {
          const tmpTool = {
            ...tool,
            healthTools: tool.healthTools.map((item) => ({
              ...item,
              icon: item.icon || LOGO,
            })),
          }
          items.push(tmpTool)
        }
        return items
      },
      [],
    )
  }, [healthToolsByCategory])
  const [isTopBarAppears, setIsTopBarAppears] = useState(false)
  const [visibleSection, setVisibleSection] = useState('')
  const displayList = (() => {
    const temp: HealthTool[] = []
    healthToolsByCategory.map((cate) => {
      cate.healthTools.map((i) => {
        if (DISLAY_TOOL.filter((j) => j.name === i.template).length > 0) {
          temp.push(i)
        }
      })
    })
    return temp
  })()

  return (
    <NavigatorLayout
      title={f(
        {
          id: 'healthToolsPage.seoTitle',
        },
        {
          siteName,
        },
      )}
      description={f({ id: 'HealthToolsPage.seoDescription' })}
      onTopBarAppears={setIsTopBarAppears}
      data-is-top-bar={isTopBarAppears}
      image={`${getHelloSitesUrl(
        locale,
      )}${assetImagePath}/thumbnail-healthtool.png`}
      SEOData={{
        type: 'article',
        schemaData: f({ id: 'healthToolsPage.seoSchema' }),
        isIndexable: true,
      }}
      footerInfo={footerInfo}
      dataNavMenuContent={dataNavMenuContent}
      languageOptions={getDefaultLanguageOptions(locale, asPath)}
      activeMenuService="tools"
      subHead={
        <SubHeader
          sx={{
            transition: 'transform .5s ease',
            transform: `translateY(${entry?.isIntersecting ? '-120%' : 0})`,
          }}
        >
          <Container>
            <HealthToolsNav
              options={options}
              visibleSection={visibleSection}
              styles={(theme) => ({
                nav: {
                  position: 'relative',
                  borderBottom: `1px solid ${theme.colors.neutral[1]}`,
                },
                navItem: {
                  padding: '16px 0 10px',
                  borderBottom: '2px solid transparent',
                  whiteSpace: 'nowrap',
                  cursor: 'pointer',
                  '&.selected': {
                    borderBottomColor: theme.fn.primaryColor(),
                    color: theme.fn.primaryColor(),
                  },
                  [theme.fn.smallerThan('sm')]: {
                    '&:last-child': {
                      paddingRight: '16px',
                    },
                  },
                },
              })}
            />
          </Container>
        </SubHeader>
      }
    >
      <StyledHealthToolsPageHome>
        <HealthToolBreadcrumb
          pageItem={[
            {
              label: f({ id: 'healthtools.title' }),
              value: '#',
            },
          ]}
        />
        <IntroToolsBanner healthools={displayList} />
        <HealthToolLayout>
          <HealthToolLayout.LeftColumn>
            <Box>
              <Stack spacing={8}>
                <Text size="h4">
                  {f({ id: 'healthToolsPage.allHealthTools' })}
                </Text>
                <Text size="p4">{f({ id: 'healthToolsPage.desciption' })}</Text>
              </Stack>
              <Stack spacing={34}>
                <div ref={ref}>
                  <HealthToolsNav
                    options={options}
                    visibleSection={visibleSection}
                  />
                </div>
                <ScrollingBlocks
                  unsetHash
                  onVisible={setVisibleSection}
                  className={classes.body}
                >
                  {options.map((i, idx) => (
                    <div
                      key={idx}
                      className={classes.group}
                      data-hashtag={String(i.termId)}
                      data-id-scroll={String(i.termId)}
                      data-gap={80}
                    >
                      <Text size="h5" as="h2">
                        {innertext(i.name)}
                      </Text>
                      <HealthToolTypeCategory healthTools={i.healthTools} />
                    </div>
                  ))}
                </ScrollingBlocks>
              </Stack>
            </Box>
          </HealthToolLayout.LeftColumn>
        </HealthToolLayout>
        <MobileBottomNavigationWrapper />
      </StyledHealthToolsPageHome>
    </NavigatorLayout>
  )
}
